import React, { useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade, Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import './OzelliklerCarousel.css';

export interface CarouselItem {
  title: string;
  description: string;
  id: number;
  icon: string;
  name: string;
  image?: string;
}

export interface OzelliklerCarouselProps {
  items: CarouselItem[];
  autoplay?: boolean;
  autoplayDelay?: number;
  pauseOnHover?: boolean;
  loop?: boolean;
}

const OzelliklerCarousel: React.FC<OzelliklerCarouselProps> = ({
  items,
  autoplay = true,
  autoplayDelay = 5000,
  pauseOnHover = true,
  loop = true
}) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMouseOverRef = useRef(false);

  useEffect(() => {
    if (swiperRef.current && pauseOnHover) {
      const swiper = swiperRef.current;
      const container = swiper.el;

      const handleMouseEnter = () => {
        isMouseOverRef.current = true;
        swiper.autoplay.stop();
      };

      const handleMouseLeave = () => {
        isMouseOverRef.current = false;
        swiper.autoplay.start();
      };

      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [pauseOnHover]);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const handleWheel = (e: WheelEvent) => {
      if (!swiperRef.current || !isMouseOverRef.current) return;

      const swiper = swiperRef.current;

      const realIndex = swiper.realIndex !== undefined ? swiper.realIndex : swiper.activeIndex;
      const isLastSlide = realIndex === items.length - 1;
      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;

      if (isLastSlide && isScrollingDown) {
        return;
      }

      if (realIndex === 0 && isScrollingUp) {
        return;
      }

      if (isScrollingDown) {
        if (swiper.allowSlideNext) {
          e.preventDefault();
          e.stopPropagation();
          swiper.slideNext();
        }
      } else if (isScrollingUp) {
        if (swiper.allowSlidePrev) {
          e.preventDefault();
          e.stopPropagation();
          swiper.slidePrev();
        }
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [items.length]);

  return (
    <div className="blog-slider" ref={containerRef}>
      <Swiper
        modules={[Autoplay, Pagination, EffectFade, Mousewheel]}
        spaceBetween={30}
        effect="fade"
        fadeEffect={{
          crossFade: true
        }}
        loop={loop}
        mousewheel={{
          enabled: false,
        }}
        autoplay={autoplay ? {
          delay: autoplayDelay,
          disableOnInteraction: false,
        } : false}
        pagination={{
          el: '.blog-slider__pagination',
          clickable: true,
        }}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper: SwiperType) => {
          swiperRef.current = swiper;
        }}
        className="blog-slider__wrp"
      >
        {items.map((item) => (
          <SwiperSlide key={item.id} className="blog-slider__item">
            <div className="blog-slider__img">
              {item.image ? (
                <img src={item.image} alt={item.title} loading="lazy" />
              ) : (
                <img src={`/assets/icons/${item.icon}`} alt={item.title} loading="lazy" />
              )}
            </div>
            <div className="blog-slider__content">
              <div className="blog-slider__title">{item.title}</div>
              <div className="blog-slider__text">{item.description}</div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="blog-slider__pagination"></div>
    </div>
  );
};

export default OzelliklerCarousel;
